import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
  ALERT_CENTER_NS,
  PAST_DUE_SUBSCRIPTION_TARGET,
} from 'src/alerts/AlertCenter.constants';
import { inspectStripeInvoiceUrl } from 'src/providers/AlertCenterProvider/PastDueSnapshot.functions';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import type { tAlertDetailRendererProps } from './AlertDetail.registry';
import AlertDetailBody from './AlertDetailBody.component';

/**
 * Rich body for the day 9+ past-due rung (U14/RET-1269) — the one rung whose
 * CTA has to be able to carry a charge.
 *
 * The Alert Center has no full-screen placement (the closed set is
 * `wide | slot | contextual | in-page`, and a P0 must be literal `wide`), so
 * the mockup's full-screen block ships as the wide strip PLUS this detail —
 * the shipped pattern for `account-health` and `la-usage-capped`.
 *
 * 🔴 IT ADDS EXACTLY TWO FACTS, and adding a third would be the known defect.
 *
 * The rung's own body already carries the decline reason, the attempt count,
 * the next retry and (from day 5) the cancellation deadline. An early draft
 * re-rendered those beneath it as stat chips, which said every fact twice —
 * and, because the chips read a frozen value while the body computed one,
 * said the retry date DIFFERENTLY (prototype, 2026-09-11: body "12 sep", chip
 * "11 de septiembre"). None of those four is repeated here.
 *
 * What IS here is the two things that enumeration leaves out: the AMOUNT OWED
 * (see below — no string on this rung interpolates it) and HOW MANY PEOPLE
 * lose access when the cancellation lands.
 *
 * That count comes from `workspaceInfo.users`, the same map Leadsales already
 * reports about this workspace as `workspace_member_count` and Intercom's
 * "Registered Users" — so the number the owner reads here matches the number
 * we report about them everywhere else. It is NOT filtered by `isInactive`:
 * that flag is the sidebar availability toggle (Activo/Inactivo), not access,
 * and filtering on it would UNDERCOUNT the people who actually lose the
 * account. No users map (or an empty one) renders no line at all, rather than
 * a claim about zero people.
 *
 * 🔴 IT CLEARS ITSELF WHEN THE PAYMENT LANDS — FROM OUTSIDE THIS COMPONENT.
 *
 * `SubscriptionProvider` has no poll, the hosted invoice opens in a NEW TAB,
 * and the return to this tab is the only signal that the customer paid. That
 * `visibilitychange` refetch used to live HERE, and it was wrong here: closing
 * this dialog — the X, Escape, a backdrop click, all of which reach
 * `AlertDetailModal`'s `requestClose` — unmounted the only mechanism that
 * could clear the notice, so an owner who paid and then tidied the dialog away
 * came back to a full-width strip they cannot dismiss, announcing a
 * cancellation that is no longer coming.
 *
 * It now lives in `useAlertCenterValue` (`AlertCenterProvider`), which is
 * mounted for the whole private session, so the refresh is alive whether this
 * detail is open, closed, or never opened. 🔴 Do not re-add a listener here:
 * two owners of the same signal means two summary requests per return.
 */
export default function PastDueFinalDetail(props: tAlertDetailRendererProps) {
  const { t } = useTranslation(ALERT_CENTER_NS);
  const navigate = useNavigate();
  const { subscription } = useSubscriptionContext();
  const { workspaceInfo } = useWorkspaceContext();
  const { onRequestClose } = props;

  /*
   * ONE derivation, not a second guard. `resolvePastDueSnapshot` publishes
   * `pastDueInvoiceUrl` through this exact function, so the button below and
   * the quiet rungs' `external` CTA cannot disagree about what is payable —
   * the same reason `AccountHealthDetail` re-derives its variant through the
   * registry's own resolver instead of re-deciding it.
   *
   * The guard is an ORIGIN allowlist (Stripe hosted-invoice origins only;
   * credentials, ports and non-https refused), and it is not re-stated or
   * relaxed here.
   */
  const invoiceUrl = inspectStripeInvoiceUrl(subscription?.hostedInvoiceUrl).url;

  /*
   * THE AMOUNT OWED — the one published field the rung's own copy never says.
   *
   * Its 🔴 no-restatement note enumerates what the body carries (decline
   * reason, attempt count, next retry, cancellation deadline) and the amount
   * is not among them: `pastDue.owner.final.{title,titleNoDate,body}` carry no
   * `{{amount}}`, so an owner who saw the figure on the day 0-4 rung
   * ("No pudimos cobrar tu suscripción de $X") loses it on the rung where
   * they are actually being asked to pay. Rendering it here restates nothing.
   *
   * Read from the LIVE alert's published values, which is the snapshot's
   * `pastDueAmount` verbatim — already formatted (minor units → display string
   * by `formatInvoiceAmount`, in `resolvePastDueSnapshot`) and NEVER
   * re-formatted here. `AlertDetailModal` re-reads the alert from
   * `derivedAlerts` every render, so this tracks the snapshot rather than
   * freezing a value at open time: that frozen-second-source is exactly what
   * made the prototype's chips disagree with the body.
   *
   * Deriving it instead from `subscription.amountRemaining` would mean a
   * second `formatInvoiceAmount` call with a second locale source — the same
   * drift, one layer down.
   */
  const publishedAmount = props.alert.copy.values?.amount ?? null;

  const memberCount = workspaceInfo?.users
    ? Object.keys(workspaceInfo.users).length
    : 0;

  function handlePay() {
    if (!invoiceUrl) return;
    // The detail deliberately stays OPEN. Closing it no longer costs the
    // return-to-tab refresh (the provider owns that now), but an owner who
    // pays from a second tab and comes back still expects to find the screen
    // they left — and closing it for them would look like the alert resolved
    // before the payment had.
    window.open(invoiceUrl, '_blank', 'noopener,noreferrer');
  }

  function handleManage() {
    // Router navigation, never `window.location`: a full reload tears down
    // every provider the shell just hydrated.
    navigate(PAST_DUE_SUBSCRIPTION_TARGET);
    onRequestClose();
  }

  return (
    <div className="AlertDetail" data-testid="alert-detail-past-due-final">
      <AlertDetailBody alert={props.alert} />

      {/* Null published amount renders NO line — never "$" alone, never a
          placeholder. Same discipline as the refused-URL branch below: a fact
          we do not have is a fact we do not state. */}
      {publishedAmount !== null && publishedAmount !== '' ? (
        <p
          className="AlertDetail__Body"
          data-testid="alert-detail-past-due-amount"
        >
          <Trans
            t={t}
            i18nKey="detail.pastDueFinal.amountOwed"
            values={{ amount: publishedAmount }}
            components={{ b: <b /> }}
          />
        </p>
      ) : null}

      {memberCount > 0 ? (
        <p
          className="AlertDetail__Note"
          data-testid="alert-detail-past-due-users"
        >
          {t('detail.pastDueFinal.usersAtRisk', { count: memberCount })}
        </p>
      ) : null}

      {/*
       * ONE action row, always — the footer invariant reads the declaration's
       * `kind: 'detail'` and suppresses its own CTA on that basis alone, so a
       * branch that painted nothing here would leave the loudest rung in the
       * system with no way out but the close button.
       *
       * Which door it is depends on what the BFF published. No hosted invoice
       * (manual collection, or a URL the origin guard refused) degrades to
       * Ajustes > Suscripción, exactly as `pastDuePayCta` does for the quiet
       * rungs — never a pay button that opens nothing.
       */}
      <div className="AlertDetail__Actions">
        {invoiceUrl ? (
          <button
            type="button"
            className="AlertDetail__OptionCta AlertDetail__OptionCta--primary"
            onClick={handlePay}
            data-testid="alert-detail-past-due-pay"
          >
            {t('pastDue.cta.pay')}
          </button>
        ) : (
          <button
            type="button"
            className="AlertDetail__OptionCta AlertDetail__OptionCta--primary"
            onClick={handleManage}
            data-testid="alert-detail-past-due-manage"
          >
            {t('pastDue.cta.manage')}
          </button>
        )}
      </div>
    </div>
  );
}
